import React from 'react';
import { Path, Svg } from 'react-native-svg';
import type { IconProps } from './types';
import { Colors } from '../../../themes/colors';

/**
 * Sun, marking the Appearance row in Settings.
 *
 * This is design's SVG exactly as supplied: the same path, the same 16x16
 * viewBox, the same 1.5 stroke, and butt caps. Nothing here is "improved".
 *
 * `strokeLinecap="butt"` is NOT redundant, even though butt is the SVG default.
 * The shared `Icon` wrapper injects `strokeLinecap="round"` into every icon it
 * renders, and because this path sets no cap of its own that value inherits from
 * the <Svg> down to the <Path> - which rounded the ray ends and made the glyph
 * read nothing like the artwork. Stating the cap here overrides the wrapper, as
 * its own comment invites individual icons to do.
 *
 * The rays end exactly on 0 and 16, and butt caps terminate at the coordinate
 * instead of overshooting it, so the tight viewBox clips nothing.
 */
export const SunIcon: React.FC<IconProps> = ({
  size = 24,
  color = Colors.role.textStrong,
  ...props
}) => (
  <Svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    // No `overflow` escape hatch, and so no need for the `as unknown` style
    // cast the older icons carry (which is itself a type error): butt caps on
    // the box edge do not overshoot, so there is nothing to let through.
    {...props}
  >
    <Path
      d="M8.00368 0V2.90909M13.0909 7.99645H16M8.00373 13.0909V16M0 7.99645H2.90909M13.6595 2.34575L11.6024 4.40279M11.6023 11.5973L13.6594 13.6543M4.40281 11.6025L2.34577 13.6595M2.34565 2.34063L4.40268 4.39767M11.6364 7.99645C11.6364 10.0048 10.0083 11.6328 8 11.6328C5.99169 11.6328 4.36364 10.0048 4.36364 7.99645C4.36364 5.98814 5.99169 4.36009 8 4.36009C10.0083 4.36009 11.6364 5.98814 11.6364 7.99645Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="butt"
    />
  </Svg>
);

SunIcon.displayName = 'SunIcon';
